export default class OMT_UI_DrawUtil {
  /**
   * Creates a nineslice box of varying width. Useful for images that need a tile visual but nineslice for elasticity
   * @param {Object} config
   * @param {string} config.spreadsheetName
   * @param {string} config.assetName
   * @param {Phaser.Rectangle} config.sliceDimensions
   * @param {number} config.bodyWidth
   * @param {?number} config.offset
   * @param {?number} config.offset.left
   * @param {?number} config.offset.right
   * @returns {Phaser.Group}
   */
  static drawNineSliceTileBox(config) {
    const rightSide = new Phaser.Image(game, 0, 0, config.spreadsheetName, config.assetName); // Make the right side first so I can copy its texture

    const targetSlice = config.sliceDimensions;
    const leftSide = new Phaser.Image(game, 0, 0, rightSide.generateTexture(1, PIXI.scaleModes.DEFAULT, game.renderer)); // Make new image with copied texture
    leftSide.crop(new Phaser.Rectangle(0, 0, targetSlice.left, leftSide.height)); // Crop out the rest of the part. Just keep the left side
    leftSide.anchor.set(0.5);
    leftSide.x = (leftSide.width - config.bodyWidth) / 2;

    const middleTexture = rightSide.generateTexture(1, PIXI.scaleModes.DEFAULT, game.renderer); // Copy texture so I can crop it
    middleTexture.crop = new Phaser.Rectangle(targetSlice.left, 0, middleTexture.width - targetSlice.right - targetSlice.left, middleTexture.height); // Get the middle
    const middleSide = new Phaser.TileSprite(game, 0, 0, 0.5 + config.bodyWidth - targetSlice.left - targetSlice.right, middleTexture.height, middleTexture); // Tilesprite using texture
    middleSide.anchor.set(0.5);
    middleSide.tileScale.x = 0.5;
    middleSide.x = leftSide.x + (leftSide.width + (middleSide.width / 2)) / 2;

    // Finally use the image and just modify it.
    rightSide.crop(new Phaser.Rectangle(rightSide.width - targetSlice.right, 0, targetSlice.right, rightSide.height), true); // Crop out the not-right side
    rightSide.anchor.set(0.5);
    rightSide.x = (config.bodyWidth - rightSide.width) / 2;

    if (config.offset && config.offset.left) {
      leftSide.x += config.offset.left;
    }
    if (config.offset && config.offset.right) {
      rightSide.x += config.offset.right;
    }

    const border = new Phaser.Group(game, null); // Combine!
    border.addChild(leftSide);
    border.addChild(middleSide);
    border.addChild(rightSide);
    return border;
  }

  /**
   *
   * @param {Object} config
   * @param {Phaser.Rectangle} config.iconDimension
   * @param {number} config.width
   * @param {Array<{ amount:number, prize:string }>} config.prizes
   * @param {Object} config.textStyle
   * @param {string} config.textStyle
   */
  static drawDynamicPrizes(config) {
    const givenWidth = config.width;
    const prizeSection = new Phaser.Group(G.game, null);
    const iconDimensions = config.iconDimension;

    const prizes = []; // Array of row elements
    const makeNewRowElement = () => ({ width: 0, elements: [] }); // Returns the bare minimum for row positioning
    let rowElements = makeNewRowElement();
    const horizontalSpacing = 30;
    for (let i = 0; i < config.prizes.length; i++) {
      const curPrize = config.prizes[i];
      const prizeSect = new Phaser.Group(G.game, null);
      let isCoin = false; // Its not a coin~ :J
      let icon;
      switch (curPrize.prize) {
        case 'coin':
          icon = G.makeImage(0, 0, 'coin_3', [0, 0.5], prizeSect);
          isCoin = true; // It is a coin! O_O;
          break;
        case 'life':
          icon = G.makeImage(0, 0, 'heart', [0, 0.5], prizeSect);
          break;
        default: // Perhaps bad, but default is assuming booster
          icon = G.makeImage(0, 0, this.getGiftString(curPrize.prize), [0, 0.5], prizeSect); // Get the correct booster image
          break;
      }
      icon.width = icon.height = iconDimensions.width;

      let amountText;
      if (isCoin) { // Coin gets a different text
        amountText = new G.Text(Math.round(iconDimensions.width) + 5, 0, `${curPrize.amount}`, config.textStyle, [0, 0.5], givenWidth, 120, true, 'center');
      } else {
        amountText = new G.Text(Math.round(iconDimensions.width) + 5, 0, `x ${curPrize.amount}`, config.textStyle, [0, 0.5], givenWidth, 120, true, 'center');
      }
      prizeSect.addChild(amountText);

      if (rowElements.width + prizeSect.width + horizontalSpacing > givenWidth) { // If everything combined exceeds the given width
        prizes.push(rowElements); // The current row is pushed into prizes
        rowElements = makeNewRowElement(); // A new current row is made
      }
      rowElements.width += prizeSect.width; // The width is added
      rowElements.elements.push(prizeSect); // The element is added to the current row
    }
    if (rowElements.width > 0) { // If theres any leftover elements
      prizes.push(rowElements);
    }

    // Now positioning elements in their rows
    let nextY = 0;
    for (let i = 0; i < prizes.length; i++) { // For all rows
      const elements = prizes[i]; // Element row
      // Dynamically finding out the horizontal spacing between elements (or 0 if just 1 element)
      const horiSpacing = elements.elements.length > 1 ? ((elements.width / 2) / elements.elements.length) / 2 : 0;
      let nextX = -(elements.width + horiSpacing) / 2; // Offsetting the initial X
      for (let j = 0; j < elements.elements.length; j++) { // For all elements in element row
        const elem = elements.elements[j];
        elem.x = Math.round(nextX);
        elem.y = Math.round(nextY);
        nextX += elem.width + horiSpacing;
        prizeSection.addChild(elem);
      }
      nextY += iconDimensions.height + 5;
    }

    return prizeSection;
  }

  /**
   * Static function used by most of RequestHelp feature. Gets the asset string
   * @param {string} str
   */
  static getGiftString(str) {
    let giftString = '';
    if (str.indexOf('#9') > -1) {
      giftString = 'ui_booster_9';
    } else {
      giftString = G.gift.getIconForType(str);
    }

    return giftString;
  }
}
